import { useEffect } from 'react'

import { usePortalSearchUnits, usePortalSearchUnitsCount } from '@apps-common/apis/portal/unit'
import { PortalUnitsHeader } from '@apps-common/apps/portal/pages/units/components'
import { buildTableKeys } from '@apps-common/apps/portal/pages/units/components/portal/units-table/table-keys'
import { RootHeaderButtons } from '@apps-common/components/common'
import { Table } from '@apps-common/components/elements/table'
import { LayoutContainer, LayoutPadding } from '@apps-common/components/layout'
import { ModalButton, PortalUnitsFilterExportModal } from '@apps-common/components/modals'
import { PageClearFilterButton, PageResultsCount } from '@apps-common/components/page'
import { commonIcons } from '@apps-common/config/icons'
import { MODAL_NO_BLUR } from '@apps-common/config/modal'
import { snakeToSentence } from '@apps-common/helpers/snakeCaseToSentence'
import { useFilterModalAnimation } from '@apps-common/hooks/useFilterModalAnimation'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { usePortalFirebaseUser } from '@apps-common/hooks/usePortalFirebaseUser'
import { usePortalUnitsFilterStore } from '@apps-common/stores'

export const PortalUnitsTableView = () => {
  const user = usePortalFirebaseUser()
  const navigation = useNavigation()
  const { navigateForward } = navigation
  const filter = usePortalUnitsFilterStore()
  const modalAnimations = useFilterModalAnimation({ desktopCss: MODAL_NO_BLUR.cssClass })

  const {
    isLoading: countIsLoading,
    isError: countIsError,
    data: countData
  } = usePortalSearchUnitsCount({
    model: filter
  })

  const {
    isLoading: unitsLoading,
    isFetching: unitsFetching,
    isRefetching: unitsRefetching,
    isError: unitsError,
    data: units
  } = usePortalSearchUnits({
    model: filter
  })

  useEffect(() => {
    const element = document.getElementById('portal-units-table-container')

    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }, [unitsLoading])

  const countLoading = countIsLoading || countIsError
  const loading = unitsLoading || unitsFetching || unitsRefetching || unitsError || countLoading

  return (
    <>
      <PortalUnitsHeader
        icons={(<RootHeaderButtons />) as any}
        leftComponent={
          <PageResultsCount loading={countLoading} count={countData} extraText={snakeToSentence(filter.status)}>
            |&nbsp;
            <ModalButton
              classes='jost underline text-text !px-0 !py-0 rounded-full flex items-center justify-start gap-[5px]'
              component={PortalUnitsFilterExportModal}
              disabled={false}
              cssClass={modalAnimations.css}
              enterAnimation={modalAnimations.enterAnimation}
              leaveAnimation={modalAnimations.leaveAnimation}
            >
              Export
              <commonIcons.export />
            </ModalButton>
          </PageResultsCount>
        }
        rightComponent={<PageClearFilterButton filter={filter} filterKeyWordsFunc={filter.getFilteredByKeywords} callback={filter.clear} />}
        loading={countLoading}
        navigateForward={navigateForward}
        searchCount={countData?.resultCount || -1}
      />
      <LayoutContainer id='portal-units-table-container'>
        <LayoutPadding topPadding='pt-[20px] md:pt-[20px]'>
          <div className='w-full'>
            <Table
              loading={loading}
              loadingSkeletonHeight='!leading-[36px]'
              placeholderSkeletonHeight='!leading-[36px]'
              pagination={{
                loading: countLoading,
                count: countData?.resultCount,
                skip: filter.skip,
                take: filter.take,
                onSkipChanged: (value) => filter.setSkip(value),
                onTakeChanged: (value) => filter.setTake(value)
              }}
              keysId={`${filter.status}-${user?.isMonitoringCentre}`}
              keys={buildTableKeys(navigation, filter, user?.isMonitoringCentre)}
              data={units}
              onSkipChanged={(value) => filter.setSkip(value)}
              onTakeChanged={(value) => filter.setTake(value)}
            />
          </div>
        </LayoutPadding>
      </LayoutContainer>
    </>
  )
}
